import React from "react";
import ReactMarkdown from "react-markdown";
import type { Components, Options as MarkdownOptions } from "react-markdown";
import remarkGfm from "remark-gfm";
import remarkSectionize from "remark-sectionize";
import remarkSqueezeParagraphs from "remark-squeeze-paragraphs";
import rehypeSlug from "rehype-slug";
import rehypeAutolinkHeadings from "rehype-autolink-headings";
import { classNames } from "../styles/classNames";

interface MarkdownRendererProps {
  content: string;
  className?: string;
  collapsibleHeadings?: boolean;
  collapsibleDefaultOpen?: boolean;
  /** Syntax a trusted text adds, such as the help's callouts; keep the array stable. */
  extraRemarkPlugins?: NonNullable<MarkdownOptions["remarkPlugins"]>;
  /** How some elements render, such as links that stay in the app; keep the object stable. */
  components?: Components;
}

const NO_PLUGINS: NonNullable<MarkdownOptions["remarkPlugins"]> = [];

type HeadingTag = "h1" | "h2" | "h3" | "h4" | "h5" | "h6";

const HEADING_TAGS = new Set<HeadingTag>(["h1", "h2", "h3", "h4", "h5", "h6"]);

function isHeadingElement(node: React.ReactNode): node is React.ReactElement {
  return React.isValidElement(node) && typeof node.type === "string" && HEADING_TAGS.has(node.type as HeadingTag);
}

interface FoldableSectionProps {
  children: React.ReactNode;
  defaultOpen: boolean;
}

type FoldableHeadingProps = React.HTMLAttributes<HTMLHeadingElement> & {
  className?: string;
};

function FoldableSection({ children, defaultOpen }: FoldableSectionProps) {
  const [open, setOpen] = React.useState(defaultOpen);
  const items = React.Children.toArray(children);
  const headingNode = items[0];
  const sectionBody = items.slice(1);

  if (!isHeadingElement(headingNode)) {
    return <section>{children}</section>;
  }

  const headingElement = headingNode as React.ReactElement<FoldableHeadingProps>;

  const heading = React.cloneElement(headingElement, {
    className: classNames(
      headingElement.props.className,
      "app-markdown-fold-heading",
      !open && "app-markdown-fold-heading-collapsed"
    ),
    role: "button",
    tabIndex: 0,
    "aria-expanded": open,
    onClick: (event: React.MouseEvent<HTMLHeadingElement>) => {
      headingElement.props.onClick?.(event);
      if (!event.defaultPrevented) {
        setOpen((previous) => !previous);
      }
    },
    onKeyDown: (event: React.KeyboardEvent<HTMLHeadingElement>) => {
      headingElement.props.onKeyDown?.(event);
      if (event.defaultPrevented) {
        return;
      }
      if (event.key === "Enter" || event.key === " ") {
        event.preventDefault();
        setOpen((previous) => !previous);
      }
    },
  });

  return (
    <section className="app-markdown-fold-section">
      {heading}
      {open ? sectionBody : null}
    </section>
  );
}

function MarkdownRenderer({
  content,
  className = "prose prose-sm w-full max-w-none break-words",
  collapsibleHeadings = false,
  collapsibleDefaultOpen = true,
  extraRemarkPlugins = NO_PLUGINS,
  components: extraComponents,
}: MarkdownRendererProps) {
  const remarkPlugins = React.useMemo(
    () =>
      collapsibleHeadings
        ? [remarkGfm, ...extraRemarkPlugins, remarkSqueezeParagraphs, remarkSectionize]
        : [remarkGfm, ...extraRemarkPlugins],
    [collapsibleHeadings, extraRemarkPlugins]
  );

  // Raw HTML in the content is not rendered (no rehype-raw): content can be model output.
  const rehypePlugins = React.useMemo<MarkdownOptions["rehypePlugins"]>(
    () =>
      collapsibleHeadings
        ? [rehypeSlug]
        : [rehypeSlug, [rehypeAutolinkHeadings, { behavior: "wrap" }]],
    [collapsibleHeadings]
  );

  const components = React.useMemo<Components | undefined>(() => {
    if (!collapsibleHeadings) {
      return extraComponents;
    }

    return {
      ...extraComponents,
      section: ({ children }) => {
        return <FoldableSection defaultOpen={collapsibleDefaultOpen}>{children}</FoldableSection>;
      },
    };
  }, [collapsibleDefaultOpen, collapsibleHeadings, extraComponents]);

  return (
    <article className={classNames("app-markdown", className)}>
      <ReactMarkdown
        remarkPlugins={remarkPlugins}
        rehypePlugins={rehypePlugins}
        components={components}
      >
        {content}
      </ReactMarkdown>
    </article>
  );
}

export default React.memo(MarkdownRenderer);
